import { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetTimereports, useGetHolidays, useGetLeaveSettings } from '../../../Core/Hooks/useTimereport';
import { useTimereportSettings } from '../../../Core/Hooks/admin/useTimereportSettings';
import { TimereportLeaveActivitySettings } from '../../../Core/Types/admin/timereport';
import './LeaveOverview.scss';

type LeaveOverviewProps = {
	year: number;
};

type DayCellType =
	| 'weekend'
	| 'holiday'
	| 'vacation'
	| 'sick-leave'
	| 'parental-leave'
	| 'saved-hours'
	| 'leave-of-absence'
	| 'empty';

type DayCell = {
	day: number;
	type: DayCellType;
	pending: boolean;
	dateStr: string;
	holidayHours?: number;
	leaveType?: DayCellType;
	tooltip?: string;
};

const matchesLeaveType = (activityId: number, typeId: number, setting?: TimereportLeaveActivitySettings): boolean => {
	if (!setting || (!setting.activityId && !setting.typeId)) return false;
	return (setting.activityId > 0 && setting.activityId === activityId) || (setting.typeId > 0 && setting.typeId === typeId); //should we compare type ids here??? means we have to remember not to share types between absence activities which might happen
};

const LeaveOverview = ({ year }: LeaveOverviewProps) => {
	const { t, i18n } = useTranslation();
	const start = `${year}-01-01`;
	const end = `${year}-12-31`;

	const { timereports } = useGetTimereports(start, end);
	const { holidays } = useGetHolidays();
	const { leaveSettings } = useGetLeaveSettings();
	const { settings } = useTimereportSettings();

	const weekendDaysEnabled = settings?.weekendDaysEnabled ?? false;

	const hoursPerDay = settings?.hoursPerDay ?? 8;
	const locale = i18n.resolvedLanguage?.startsWith('sv') ? 'sv-SE' : 'en-GB';

	const monthFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long' }), [locale]);

	const legendItems = useMemo<{ label: string; color: string; type: DayCellType }[]>(
		() => [
			{ label: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.VACATION'), color: '#92d042', type: 'vacation' },
			{ label: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.SICK_LEAVE'), color: '#d66b8f', type: 'sick-leave' },
			{ label: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.PARENTAL_LEAVE'), color: '#e8a813', type: 'parental-leave' },
			{
				label: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.WITHDRAWAL_OF_HOURS'),
				color: '#8abcea',
				type: 'saved-hours',
			},
			{
				label: t('TIMEREPORT_USER.OVERVIEW.SHARED.LEAVE_TYPES.LEAVE_OF_ABSENCE'),
				color: '#c9c9c9',
				type: 'leave-of-absence',
			},
			{ label: t('TIMEREPORT_USER.OVERVIEW.LEAVE_OVERVIEW.LEGEND.HOLIDAY'), color: '#dc3545', type: 'holiday' },
		],
		[t]
	);

	const isSettingConfigured = (setting?: TimereportLeaveActivitySettings): boolean => {
		return !!setting && ((setting.activityId ?? 0) > 0 || (setting.typeId ?? 0) > 0);
	};

	const visibleLegendItems = useMemo(() => {
		if (!leaveSettings) return legendItems;
		return legendItems.filter((item) => {
			switch (item.type) {
				case 'vacation':
					return (
						isSettingConfigured(leaveSettings.paidVacation) ||
						isSettingConfigured(leaveSettings.unpaidVacation) ||
						isSettingConfigured(leaveSettings.advanceVacation)
					);
				case 'sick-leave':
					return isSettingConfigured(leaveSettings.sickLeave);
				case 'parental-leave':
					return isSettingConfigured(leaveSettings.parentalLeave);
				case 'saved-hours':
					return isSettingConfigured(leaveSettings.savedHours);
				case 'leave-of-absence':
					return isSettingConfigured(leaveSettings.leaveOfAbsence);
				default:
					return true;
			}
		});
	}, [legendItems, leaveSettings]);

	const holidayMap = useMemo(() => {
		const map = new Map<string, number>();
		holidays?.forEach((h) => {
			const d = h.date.split('T')[0];
			if (d.startsWith(String(year))) {
				map.set(d, (map.get(d) ?? 0) + h.hours);
			}
		});
		return map;
	}, [holidays, year]);

	const timereportMap = useMemo(() => {
		const map = new Map<string, { activityId: number; typeId: number; isAttested: boolean; units: number }[]>();
		timereports?.forEach((tr) => {
			const d = tr.date.split('T')[0];
			if (!map.has(d)) map.set(d, []);
			map.get(d)!.push({ activityId: tr.activityId, typeId: tr.typeId, isAttested: tr.isAttested, units: tr.units });
		});
		return map;
	}, [timereports]);

	const getLeaveType = (activityId: number, typeId: number): DayCellType | null => {
		if (!leaveSettings) return null;

		//needs to be first because it is absence but can share typeId with other leave types and would get hit further down otherwise
		if (matchesLeaveType(activityId, typeId, leaveSettings.sickLeave)) {
			return 'sick-leave';
		}

		if (
			matchesLeaveType(activityId, typeId, leaveSettings.paidVacation) ||
			matchesLeaveType(activityId, typeId, leaveSettings.unpaidVacation) ||
			matchesLeaveType(activityId, typeId, leaveSettings.advanceVacation)
		) {
			return 'vacation';
		}
		if (matchesLeaveType(activityId, typeId, leaveSettings.parentalLeave)) {
			return 'parental-leave';
		}
		if (matchesLeaveType(activityId, typeId, leaveSettings.savedHours)) {
			return 'saved-hours';
		}
		if (matchesLeaveType(activityId, typeId, leaveSettings.leaveOfAbsence)) {
			return 'leave-of-absence';
		}
		return null;
	};

	const monthData = useMemo(() => {
		const data: { month: string; days: DayCell[] }[] = [];

		for (let m = 0; m < 12; m++) {
			const daysInMonth = new Date(year, m + 1, 0).getDate();
			const days: DayCell[] = [];

			for (let d = 1; d <= daysInMonth; d++) {
				const date = new Date(year, m, d);
				const dayOfWeek = date.getDay();
				const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
				const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

				if (!weekendDaysEnabled && isWeekend) {
					days.push({ day: d, type: 'weekend', pending: false, dateStr });
					continue;
				}

				const hHours = holidayMap.get(dateStr);
				const isFullHoliday = hHours != null && hHours >= hoursPerDay;
				const isPartialHoliday = hHours != null && hHours > 0 && !isFullHoliday;

				// Check for leave entries on this day
				const entries = timereportMap.get(dateStr);
				let leaveType: DayCellType | null = null;
				let leaveAttested = true;
				let leaveUnits = 0;
				if (entries) {
					for (const entry of entries) {
						const lt = getLeaveType(entry.activityId, entry.typeId);
						if (lt) {
							leaveType = lt;
							leaveAttested = entry.isAttested;
							leaveUnits = entry.units;
							break;
						}
					}
				}

				if (isFullHoliday) {
					days.push({
						day: d,
						type: 'holiday',
						pending: false,
						dateStr,
						tooltip: t('TIMEREPORT_USER.OVERVIEW.LEAVE_OVERVIEW.TOOLTIPS.HOLIDAY_HOURS', { hours: hHours }),
					});
					continue;
				}

				if (isPartialHoliday && leaveType) {
					const leaveLabel = legendItems.find((l) => l.type === leaveType)?.label ?? '';
					const leaveSuffix = leaveType === 'saved-hours' ? ` (${leaveUnits}h)` : '';
					days.push({
						day: d,
						type: 'holiday',
						leaveType,
						holidayHours: hHours,
						pending: !leaveAttested,
						dateStr,
						tooltip: t('TIMEREPORT_USER.OVERVIEW.LEAVE_OVERVIEW.TOOLTIPS.HOLIDAY_PLUS_LEAVE', {
							hours: hHours,
							leave: `${leaveLabel}${leaveSuffix}`,
						}),
					});
					continue;
				}

				if (isPartialHoliday) {
					days.push({
						day: d,
						type: 'holiday',
						pending: false,
						dateStr,
						holidayHours: hHours,
						leaveType: 'empty',
						tooltip: t('TIMEREPORT_USER.OVERVIEW.LEAVE_OVERVIEW.TOOLTIPS.HOLIDAY_HOURS', { hours: hHours }),
					});
					continue;
				}

				if (leaveType) {
					const leaveLabel = legendItems.find((l) => l.type === leaveType)?.label ?? '';
					const tooltip = leaveType === 'saved-hours' ? `${leaveLabel} (${leaveUnits}h)` : leaveLabel;
					days.push({ day: d, type: leaveType, pending: !leaveAttested, dateStr, tooltip });
					continue;
				}

				days.push({ day: d, type: 'empty', pending: false, dateStr });
			}

			data.push({ month: monthFormatter.format(new Date(year, m, 1)), days });
		}

		return data;
	}, [year, timereportMap, holidayMap, leaveSettings, weekendDaysEnabled, hoursPerDay, legendItems, monthFormatter, t]);

	const maxDays = 31;

	return (
		<div className='biz-leave-overview'>
			<div className='biz-leave-overview__legend'>
				{visibleLegendItems.map((item) => (
					<div key={item.type} className='biz-leave-overview__legend-item'>
						<span className='biz-leave-overview__legend-color' style={{ backgroundColor: item.color }} />
						<span>{item.label}</span>
					</div>
				))}
				{visibleLegendItems.some((item) => item.type !== 'holiday') && (
					<div className='biz-leave-overview__legend-item'>
						<span
							className='biz-leave-overview__legend-color'
							style={{
								backgroundColor: '#92d042',
								opacity: 0.7,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<i className='far fa-clock' style={{ fontSize: 9, color: 'rgba(0,0,0,1)' }} />
						</span>
						<span>{t('TIMEREPORT_USER.OVERVIEW.LEAVE_OVERVIEW.LEGEND.PENDING_APPROVAL')}</span>
					</div>
				)}
			</div>

			<div className='biz-leave-overview__grid'>
				<table className='biz-leave-overview__table'>
					<thead>
						<tr>
							<th className='biz-leave-overview__month-header'></th>
							{Array.from({ length: maxDays }, (_, i) => (
								<th key={i} className='biz-leave-overview__day-header'>
									{i + 1}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{monthData.map((row) => (
							<tr key={row.month}>
								<td className='biz-leave-overview__month-label'>{row.month}</td>
								{Array.from({ length: maxDays }, (_, i) => {
									const cell = row.days[i];
									if (!cell) {
										return (
											<td
												key={i}
												className='biz-leave-overview__day-cell biz-leave-overview__day-cell--empty'
											/>
										);
									}
									const classes = [
										'biz-leave-overview__day-cell',
										`biz-leave-overview__day-cell--${cell.type}`,
										cell.leaveType ? `biz-leave-overview__day-cell--split-${cell.leaveType}` : '',
										cell.pending ? 'biz-leave-overview__day-cell--pending' : '',
									]
										.filter(Boolean)
										.join(' ');
									return (
										<Tooltip key={i} title={cell.tooltip ?? cell.dateStr} arrow placement='top'>
											<td className={classes}>
												{cell.pending && <i className='far fa-clock biz-leave-overview__pending-icon' />}
											</td>
										</Tooltip>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default LeaveOverview;
